import type { Graphics } from 'pixi.js';
import { lerpColor } from '../../color';
import type { WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const GOUGES = [0.62, -2.7, 0.9, 0.36, -1.3, 1.1, 0.68, 0.35, 0.75, 0.3, 1.7, 1.2, 0.56, 2.45, 0.85, 0.14, -0.4, 1.7];
const BORDER = [0.25, 2.85, 3.45, 5.95];
const RAYS = 12;
const SPIRAL_TURNS = 1.25;
const SPIRAL_STEPS = 24;
const RENDA = 8;
const DASH = 0.55;
const MAX_DASHES = 160;
const NOTCHES = 16;
const WEDGES = 12;

function chip(g: Graphics, x: number, y: number, size: number, rot: number): void {
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  const l = size * 1.5;
  const w = size * 0.42;
  g.poly([x + c * l, y + s * l, x - s * w + c * l * 0.1, y + c * w + s * l * 0.1, x - c * l * 0.8, y - s * l * 0.8, x + s * w * 0.6, y - c * w * 0.6], true);
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    for (let i = 0; i < GOUGES.length; i += 3) {
      const a = GOUGES[i + 1]!;
      k.arcLine(body, r * GOUGES[i]!, a, a + GOUGES[i + 2]!);
    }
    g.stroke({ width: Math.max(1, r * 0.06), color: lerpColor(color, pal.ink, 0.5), alpha: 0.32 * alpha, cap: 'round', join: 'round' });
    for (let i = 0; i < BORDER.length; i += 2) k.arcLine(body, r * 0.86, BORDER[i]!, BORDER[i + 1]!);
    g.stroke({ width: Math.max(1, r * 0.03), color: lerpColor(color, pal.ink, 0.4), alpha: 0.28 * alpha, cap: 'round', join: 'round' });
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    const sx = body.scaleX;
    const sy = body.scaleY;
    const carve = lerpColor(fill, pal.ink, 0.55);
    k.trace(body);
    g.fill({ color: fill, alpha });
    const half = (Math.PI / RAYS) * 0.55;
    for (let i = 0; i < RAYS; i++) {
      const a = (i / RAYS) * TAU + 0.13;
      const inner = r * 0.6;
      const outer = r * (i % 2 === 0 ? 0.9 : 0.79);
      g.poly(
        [
          body.x + Math.cos(a - half) * inner * sx,
          body.y + Math.sin(a - half) * inner * sy,
          body.x + Math.cos(a) * outer * sx,
          body.y + Math.sin(a) * outer * sy,
          body.x + Math.cos(a + half) * inner * sx,
          body.y + Math.sin(a + half) * inner * sy,
        ],
        true,
      );
    }
    g.fill({ color: pal.heldShade, alpha: 0.5 * alpha });
    for (let s = 0; s <= SPIRAL_STEPS; s++) {
      const t = s / SPIRAL_STEPS;
      const a = t * SPIRAL_TURNS * TAU;
      const rr = r * 0.26 * t;
      const x = body.x + Math.cos(a) * rr * sx;
      const y = body.y + Math.sin(a) * rr * sy;
      if (s === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.stroke({ width: Math.max(1, r * 0.045), color: carve, alpha: 0.5 * alpha, cap: 'round', join: 'round' });
    k.trace(body);
    g.stroke({ width, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: lerpColor(pal.bg, pal.ink, 0.5), alpha: 0.5 * alpha });
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.07 * alpha });
    g.circle(body.x, body.y, reach).stroke({ width: 1.4, color: pal.spot, alpha: 0.5 * alpha });
    const s = Math.max(3, k.unit * 0.02);
    k.ringDots(body, reach, RENDA, spin, (x, y, _i, a) => {
      g.regularPoly(x, y, s, 4, a);
    });
    g.fill({ color: pal.bg, alpha: 0.9 * alpha });
    g.stroke({ width: 1.3, color: pal.spot, alpha: 0.85 * alpha });
    k.ringDots(body, reach, RENDA, spin, (x, y) => {
      g.circle(x, y, s * 0.3);
    });
    k.ringDots(body, reach, RENDA, spin + Math.PI / RENDA, (x, y) => {
      g.circle(x, y, Math.max(1.2, s * 0.22));
    });
    g.fill({ color: pal.spot, alpha: 0.85 * alpha });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.85 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const tone = last ? pal.spot : pal.incoming.circle;
    g.circle(x, y, radius).fill({ color: on ? tone : lerpColor(pal.bg, pal.ink, 0.4), alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.45) * alpha });
    if (on) g.circle(x, y, radius * 0.4).fill({ color: lerpColor(tone, pal.ink, 0.6), alpha: 0.7 * alpha });
    else g.circle(x, y, radius * 0.4).stroke({ width: width * 0.5, color: pal.path, alpha: 0.4 * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const step = Math.max(8, k.unit * 0.04);
    let dashes = 0;
    for (let i = 2; i < count && dashes < MAX_DASHES; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const dx = points[i]! - x0;
      const dy = points[i + 1]! - y0;
      const n = Math.floor(Math.hypot(dx, dy) / step);
      for (let s = 0; s < n && dashes < MAX_DASHES; s++, dashes++) {
        const t0 = s / n;
        const t1 = (s + DASH) / n;
        g.moveTo(x0 + dx * t0, y0 + dy * t0).lineTo(x0 + dx * t1, y0 + dy * t1);
      }
    }
    g.stroke({ width: 1.6, color: pal.path, alpha: alpha * 1.8, cap: 'round' });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 3 === 0) g.circle(x, y, size * 0.45);
    else chip(g, x, y, size, rot);
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const r = (m.r / (1.1 + 0.9 * m.grow)) * (1.4 + 0.08 * m.grow);
    g.circle(m.x, m.y, r).stroke({ width: m.width, color: pal.held, alpha: m.fade * 0.5 });
    for (let i = 0; i < NOTCHES; i++) {
      const a = (i / NOTCHES) * TAU;
      g.moveTo(m.x + Math.cos(a) * r * 0.93, m.y + Math.sin(a) * r * 0.93).lineTo(m.x + Math.cos(a) * r, m.y + Math.sin(a) * r);
    }
    g.stroke({ width: m.width * 0.6, color: pal.held, alpha: m.fade * 0.4 });
    const inner = r * 1.1;
    const outer = r * (1.24 + 0.06 * m.grow);
    const half = (Math.PI / WEDGES) * 0.4;
    for (let i = 0; i < WEDGES; i++) {
      const a = (i / WEDGES) * TAU + 0.26;
      g.poly(
        [
          m.x + Math.cos(a - half) * inner,
          m.y + Math.sin(a - half) * inner,
          m.x + Math.cos(a) * outer,
          m.y + Math.sin(a) * outer,
          m.x + Math.cos(a + half) * inner,
          m.y + Math.sin(a + half) * inner,
        ],
        true,
      );
    }
    g.fill({ color: pal.spot, alpha: m.fade * 0.25 });
  },

  trail(g, x, y, size, angle) {
    chip(g, x, y, size, angle);
  },
};
